import { injectable } from "inversify";
import { TableRepo } from "../table.repo";
import { ApiData, ApiPage } from "../../models/page.model";
import {
  ElectronicContractModel,
  CreateElectronicContractModel,
  ViewDocumentModel,
  SendOtpModel,
  SignDocumentModel,
  ElectronicContractModelFactory,
  ElectronicContractCustomerType,
  ElectronicContractTemplateDefinition,
  ElectronicContractTemplateModel,
  ElectronicContractPreview,
  ElectronicContractLifecycle,
  ElectronicContractPdf,
  ElectronicContractPdfContent,
  CompanySignElectronicContractModel,
  DemoElectronicContractBootstrapModel,
  DemoElectronicContractBootstrapResult,
} from "../../models/electronic-contract.model";
import { ApiResponse } from "../../models/response.model";
import { MatBaoConnectionSettings, MatBaoConnectionTestResult, UpdateMatBaoConnectionSettings } from "../../models/matbao-connection-settings.model";

interface _ElectronicContractGetParams {
  search?: string;
  waterUserCode?: string;
  status?: string;
  waterFactoryId?: string;
  templateId?: string;
}

@injectable()
export class ElectronicContractRepo extends TableRepo<ElectronicContractModel> {
  constructor() {
    super("/api/ElectronicContract");
  }

  getConnectionSettings(): Promise<ApiResponse<MatBaoConnectionSettings> | undefined> {
    return this.apiRepo.get<MatBaoConnectionSettings>("/api/ElectronicContractSettings/connection", undefined, true, true);
  }

  testConnectionSettings(model: UpdateMatBaoConnectionSettings): Promise<ApiResponse<MatBaoConnectionTestResult> | undefined> {
    return this.apiRepo.post<MatBaoConnectionTestResult>("/api/ElectronicContractSettings/connection/test", model, undefined, true, true);
  }

  saveConnectionSettings(model: UpdateMatBaoConnectionSettings): Promise<ApiResponse<MatBaoConnectionSettings> | undefined> {
    return this.apiRepo.post<MatBaoConnectionSettings>("/api/ElectronicContractSettings/connection", model, undefined, true, true);
  }

  async getTemplateCatalog(
    customerType: ElectronicContractCustomerType
  ): Promise<ApiResponse<ElectronicContractTemplateDefinition[]> | undefined> {
    return this.apiRepo.get<ElectronicContractTemplateDefinition[]>(
      `${this.basepath}/template-catalog`,
      { customerType }
    );
  }

  async getTemplates(
    status = "Active"
  ): Promise<ApiResponse<ElectronicContractTemplateModel[]> | undefined> {
    const response = await this.apiRepo.get<ApiData<ElectronicContractTemplateModel[]>>(
      "/api/ElectronicContractTemplate/all",
      { status }
    );
    if (!response) return undefined;
    return { ...response, result: response.result?.data ?? [] };
  }

  async previewFields(
    model: CreateElectronicContractModel
  ): Promise<ApiResponse<ElectronicContractPreview> | undefined> {
    return this.apiRepo.post<ElectronicContractPreview>(
      `${this.basepath}/preview-fields`,
      model
    );
  }

  async syncContract(id: string): Promise<ApiResponse<ElectronicContractLifecycle> | undefined> {
    return this.apiRepo.post<ElectronicContractLifecycle>(`${this.basepath}/${id}/sync`);
  }

  async getPdf(id: string): Promise<ApiResponse<ElectronicContractPdf> | undefined> {
    return this.apiRepo.get<ElectronicContractPdf>(`${this.basepath}/${id}/pdf`);
  }

  async getPdfContent(id: string): Promise<ApiResponse<ElectronicContractPdfContent> | undefined> {
    return this.apiRepo.get<ElectronicContractPdfContent>(`${this.basepath}/${id}/pdf-content`);
  }

  async companySign(
    model: CompanySignElectronicContractModel
  ): Promise<ApiResponse<ElectronicContractLifecycle> | undefined> {
    return this.apiRepo.post<ElectronicContractLifecycle>(`${this.basepath}/company-sign`, model);
  }

  async bootstrapDemoTemplates(
    model: DemoElectronicContractBootstrapModel
  ): Promise<ApiResponse<DemoElectronicContractBootstrapResult[]> | undefined> {
    return this.apiRepo.post<DemoElectronicContractBootstrapResult[]>(
      `${this.basepath}/demo/bootstrap-templates`,
      model
    );
  }

  async getData(
    index: number,
    params: _ElectronicContractGetParams,
    pageSize = 10
  ): Promise<ApiPage<ElectronicContractModel> | null> {
    const response = await this.apiRepo.get<ApiPage<ElectronicContractModel>>(
      `${this.basepath}/page`,
      {
        pageSize: pageSize,
        pageIndex: index,
        ...params,
      }
    );
    if (response?.code === "00" && response.result) {
      return response.result;
    }
    return null;
  }

  async createContract(
    model: CreateElectronicContractModel
  ): Promise<ApiResponse<ElectronicContractModel> | undefined> {
    try {
      const response = await this.apiRepo.post<ElectronicContractModel>(
        `${this.basepath}/create`,
        model
      );
      return response;
    } catch (err) {
      console.log(err);
      return {
        code: "99",
        errorMessage: "",
        result: ElectronicContractModelFactory.getEmpty(),
      };
    }
  }

  async getById(id: string): Promise<ElectronicContractModel | null> {
    try {
      const response = await this.apiRepo.get<ElectronicContractModel>(
        `${this.basepath}/${id}`
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async getByWaterUserCode(
    waterUserCode: string
  ): Promise<ElectronicContractModel | null> {
    try {
      const response = await this.apiRepo.get<ElectronicContractModel>(
        `${this.basepath}/by-water-user-code/${waterUserCode}`
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async viewDocument(model: ViewDocumentModel): Promise<string | null> {
    try {
      const response = await this.apiRepo.post<string>(
        `${this.basepath}/view-document`,
        model
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async sendOtp(model: SendOtpModel): Promise<boolean> {
    try {
      const response = await this.apiRepo.post<boolean>(
        `${this.basepath}/send-otp`,
        model
      );
      if (response?.code === "00" && response.result) {
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  async signDocument(model: SignDocumentModel): Promise<boolean> {
    try {
      const response = await this.apiRepo.post<boolean>(
        `${this.basepath}/sign-document`,
        model
      );
      if (response?.code === "00" && response.result) {
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  }
}
